import { useState, useEffect } from "react";
import { Link, useLocation } from "@/lib/router-compat";
import { Sun, Moon, Menu, X } from "lucide-react";
import netiusLogo from "@/assets/netius-logo-white.webp";
import { HELP } from "@/lib/help";

const Header = () => {
  const [isDark, setIsDark] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setIsDark(document.documentElement.classList.contains("dark"));
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const toggleTheme = () => {
    setIsDark(!isDark);
    document.documentElement.classList.toggle("dark");
  };

  const navLinks = [
    { label: "ABOUT", href: "/about" },
    { label: "SERVICES", href: "/services" },
    { label: "PROJECTS", href: "/projects" },
    { label: "FAQ", href: "/faq" },
    { label: "CONTACT", href: "/contact" },
  ];

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-gray-800 bg-black backdrop-blur-xs shadow-lg" role="banner">
      <div className="container flex h-16 md:h-20 items-center px-4 sm:px-6 lg:px-8">
        <Link 
          to="/" 
          className="flex items-center mr-auto hover:scale-105 transition-transform duration-300 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md"
          aria-label="NETIUS - Go to homepage"
        >
          <img
            src={netiusLogo}
            alt="NETIUS Logo"
            width={225}
            height={60}
            className="h-[60px] w-auto object-contain"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-6" role="navigation" aria-label="Main navigation">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.href}
              className={`text-sm font-medium uppercase transition-all duration-300 relative after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-white after:origin-bottom-right after:transition-all after:duration-300 hover:after:w-full hover:after:origin-bottom-left focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm py-1 ${
                isActive(link.href)
                  ? "text-white after:w-full"
                  : "text-gray-400 hover:text-white hover:scale-110"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <a
            href={HELP.home}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium uppercase text-gray-400 hover:text-white hover:scale-110 transition-all duration-300 relative after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-white after:origin-bottom-right after:transition-all after:duration-300 hover:after:w-full hover:after:origin-bottom-left focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm py-1"
          >
            HELP
          </a>
          <Link
            to="/quote"
            className="inline-flex items-center justify-center rounded-lg border-2 border-white bg-transparent px-6 py-2.5 text-sm font-medium uppercase text-white hover:bg-white hover:text-black hover:scale-105 hover:shadow-xl transition-all duration-300 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[44px]"
          >
            GET A QUOTE
          </Link>
          <button
            onClick={toggleTheme}
            className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md text-gray-400 hover:text-white hover:bg-gray-800 transition-all duration-300 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
          >
            {isDark ? (
              <Moon className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Sun className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </nav>

        {/* Mobile Menu Button */}
        <div className="flex items-center gap-2 lg:hidden">
          <button
            onClick={toggleTheme}
            className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md text-white hover:bg-gray-800 transition-all duration-300 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
          >
            {isDark ? (
              <Moon className="h-5 w-5" aria-hidden="true" />
            ) : (
              <Sun className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md text-white hover:bg-gray-800 transition-all duration-300 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" aria-hidden="true" />
            ) : (
              <Menu className="h-6 w-6" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <nav 
          id="mobile-menu"
          className="lg:hidden bg-zinc-900/95 backdrop-blur-xs px-4 py-4 rounded-b-lg animate-fade-in"
          role="navigation" 
          aria-label="Mobile navigation"
        >
          <div className="flex flex-col gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className={`min-h-[44px] py-3 px-4 text-sm font-medium uppercase transition-all duration-300 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring rounded-md ${
                  isActive(link.href)
                    ? "text-white bg-gray-800"
                    : "text-gray-400 hover:text-white"
                }`}
              >
                {link.label}
              </Link>
            ))}
            <a
              href={HELP.home}
              target="_blank"
              rel="noopener noreferrer"
              className="min-h-[44px] py-3 px-4 text-sm font-medium uppercase text-gray-400 hover:text-white transition-all duration-300 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring rounded-md"
            >
              HELP
            </a>
            <Link
              to="/quote"
              className="mt-2 inline-flex items-center justify-center rounded-lg border-2 border-white bg-transparent px-4 py-3 min-h-[44px] text-sm font-medium uppercase text-white hover:bg-white hover:text-black transition-all duration-300 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
            >
              GET A QUOTE
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;
